import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ChevronRight from "../../components/Atomos/Icons/ChevronRight";
import ChevronDown from "../../components/Atomos/Icons/ChevronDown";

type MenuItem = {
  name: string;
  component: React.ComponentType;
  slug?: string;
};

interface ISettingsMenuProps {
  children: React.ReactNode;
  items: MenuItem[];
  initialView?: string;
}

export default function SettingsMenu({
  children,
  items,
  initialView,
}: ISettingsMenuProps): React.ReactElement {
  const navigate = useNavigate();
  
  // Persist selected view in sessionStorage (clears when browser tab closes)
  const getInitialView = () => {
    // Prioritize URL-based view if provided
    if (initialView && items.some(item => item.name === initialView)) {
      return initialView;
    }
    const saved = sessionStorage.getItem('settingsSelectedView');
    if (saved && items.some(item => item.name === saved)) {
      return saved;
    }
    return items[0]?.name || '';
  };
  
  const [selectedView, setSelectedView] = useState<string>(getInitialView);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (initialView && items.some(item => item.name === initialView)) {
      setSelectedView(initialView);
      sessionStorage.setItem('settingsSelectedView', initialView);
    }
  }, [initialView, items]);

  // Detect mobile/tablet screen size
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 1024); // lg breakpoint
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  function isSelected(view: string): boolean {
    return selectedView === view;
  }

  function isExpanded(view: string): boolean {
    return expandedItems.has(view);
  }

  function handleItemClick(itemName: string): void {
    const item = items.find(i => i.name === itemName);
    const slug = item?.slug;
    
    if (isMobile) {
      setExpandedItems(prev => {
        const newSet = new Set(prev);
        if (newSet.has(itemName)) {
          newSet.delete(itemName);
        } else {
          newSet.add(itemName);
          // Save last expanded item in mobile view
          sessionStorage.setItem('settingsSelectedView', itemName);
          if (slug) {
            navigate(`/settings/${slug}`);
          }
        }
        return newSet;
      });
    } else {
      setExpandedItems(new Set());
      setSelectedView(itemName);
      sessionStorage.setItem('settingsSelectedView', itemName);
      // Update URL if slug exists
      if (slug) {
        navigate(`/settings/${slug}`);
      }
    }
  }

  function SelectedView(): React.ReactElement {
    return (
      <>
        {items.map((item) => {
          const Component = item.component;
          const isActive = item.name === selectedView;
          return (
            <div key={item.name} style={{ display: isActive ? 'block' : 'none' }}>
              <Component />
            </div>
          );
        })}
      </>
    );
  }

  function MobileView(): React.ReactElement {
    return (
      <div className="w-full rounded-lg border-[1px] border-gray-200">
        <div className="w-full flex flex-col">
          {items.map((item, index) => {
            const Component = item.component;
            return (
              <div key={item.name} className="border-b-[1px] border-b-gray-100">
                <div
                  className="flex flex-row w-full justify-between cursor-pointer p-4"
                  onClick={() => handleItemClick(item.name)}
                >
                  <span
                    className={`text-[#0070CD] text-sm font-decimal text-left ${
                      isExpanded(item.name) ? "text-[#0070CD]" : "text-[#0070CD]"
                    }`}
                  >
                    {item.name}
                  </span>
                  <div className="w-4 h-4">
                    {isExpanded(item.name) ? (
                      <ChevronDown className="fill-[#0070CD]" rotation="rotate(180deg)" />
                    ) : (
                      <ChevronDown className="fill-[#0070CD]" />
                    )}
                  </div>
                </div>
                <div className="px-4 pb-4" style={{ display: isExpanded(item.name) ? 'block' : 'none' }}>
                  <Component />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  function DesktopView(): React.ReactElement {
    return (
      <div className="w-full rounded-lg border-[1px] border-gray-200 flex flex-row">
        {/* Left Side Menu */}
        <div className="w-1/4 rounded-lg min-h-[15rem]">
          <div className="w-full min-w-1600px flex flex-col border-r-[1px] border-gray-50 pr-4">
            {items.map((item) => (
              <div key={item.name} className="border-b-[1px] border-b-gray-100 items-center p-4">
                <div
                  className="flex flex-row w-full justify-between cursor-pointer items-center"
                  onClick={() => handleItemClick(item.name)}
                >
                  <span
                    className={`text-[#0070CD] lg:text-[16px] xl:text-[18px] font-decimal text-left font-[400] ${
                      isSelected(item.name) ? "text[#0070CD]" : "text-[#0070CD]"
                    }`}
                  >
                    {item.name}
                  </span>
                  <div className="w-4 h-4">
                    <ChevronRight
                      className={`${isSelected(item.name) ? "fill-[#0070CD]" : "fill-[#0070CD]"}`}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="w-3/4">
          <SelectedView />
        </div>
        {children}
      </div>
    );
  }

  return isMobile ? <MobileView /> : <DesktopView />;
}
